import React from 'react';
import { Shield, Globe, Users, Target, CheckCircle, Info } from 'lucide-react';
import Navbar from '../components/accueil/Navbar';
import Footer from '../components/accueil/Footer';
import ChatWidget from '../components/accueil/ChatWidget';
import BackToTop from '../components/accueil/BackToTop';
import PageBanner from './Banner';
import useGlobale from '../components/global';

const AproScreen = () => {
  const { authenticated, user, logout } = useGlobale();

  const missions = [
    {
      icon: <Shield className="w-7 h-7" />,
      title: "Sécurité et Fiabilité",
      description:
        "Nous assurons un traitement sécurisé des données et des transactions pour tous les voyageurs.",
    },
    {
      icon: <Globe className="w-7 h-7" />,
      title: "Accessibilité Mondiale",
      description:
        "Notre plateforme est accessible 24/7 depuis n'importe où dans le monde.",
    },
    {
      icon: <Users className="w-7 h-7" />,
      title: "Service Client Dédié",
      description:
        "Une équipe multilingue à votre disposition pour vous accompagner dans vos démarches.",
    },
  ];

  const features = [
    "Procédures d'immigration simplifiées",
    "Demandes de visa en ligne",
    "Paiement sécurisé",
    "Suivi en temps réel",
    "Assistance multilingue",
    "Documentation numérique",
  ];

  const commitments = [
    {
      icon: <Target className="w-6 h-6" />,
      title: "Précision",
      description: "Traitement rapide et précis de vos demandes",
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Sécurité",
      description: "Protection maximale de vos données",
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Support",
      description: "Assistance personnalisée pour chaque voyageur",
    },
  ];

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Navbar authenticated={authenticated} user={user} logout={logout} />

      <PageBanner
        currentPage="À propos"
        title="À propos de Niger Voyage"
        subtitle="Votre plateforme officielle pour la gestion simplifiée des voyages au Niger."
        icon={<Info className="w-6 h-6" />}
      />

      {/* Section Présentation */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700 bg-brand-50 rounded-full">
            Qui sommes-nous
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-black text-slate-900">
            Une plateforme officielle, moderne et sécurisée
          </h2>
          <p className="mt-5 text-slate-600 leading-relaxed">
            Niger Voyage est la plateforme officielle du gouvernement nigérien
            dédiée à la simplification des procédures de voyage. Lancée en 2024,
            notre plateforme vise à moderniser et à faciliter toutes les
            formalités administratives liées aux voyages vers et depuis le Niger.
          </p>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Notre mission est de rendre les voyages au Niger plus accessibles et
            plus sûrs, tout en garantissant le respect des procédures
            administratives et sanitaires en vigueur.
          </p>
        </div>
      </section>

      {/* Section Missions */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {missions.map((mission, index) => (
              <div
                key={index}
                className="bg-white p-7 rounded-2xl border border-slate-200 hover:border-brand-300 hover:shadow-lg transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mb-5">
                  {mission.icon}
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">
                  {mission.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {mission.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Caractéristiques + Chiffres Clés */}
      <section className="bg-white py-16 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700 bg-brand-50 rounded-full">
                Nos services
              </span>
              <h2 className="mt-4 text-3xl md:text-4xl font-black text-slate-900 mb-6">
                Ce que nous offrons
              </h2>
              <div className="space-y-3">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-brand-50 text-brand-600 flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="w-5 h-5" />
                    </span>
                    <span className="text-slate-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-brand-600 to-brand-700 text-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-xl font-bold mb-6">Nos Chiffres Clés</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <div className="text-3xl md:text-4xl font-black mb-1">
                      10K+
                    </div>
                    <div className="text-sm text-white/80">
                      Voyageurs assistés
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-black mb-1">
                      98%
                    </div>
                    <div className="text-sm text-white/80">
                      Taux de satisfaction
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-black mb-1">
                      24/7
                    </div>
                    <div className="text-sm text-white/80">
                      Support disponible
                    </div>
                  </div>
                  <div>
                    <div className="text-3xl md:text-4xl font-black mb-1">
                      150+
                    </div>
                    <div className="text-sm text-white/80">Pays desservis</div>
                  </div>
                </div>
              </div>
              <div className="absolute -z-10 -top-4 -right-4 w-40 h-40 bg-brand-200/50 rounded-full blur-3xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Section Engagement */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="inline-block px-3 py-1 text-xs font-semibold uppercase tracking-wider text-brand-700 bg-brand-50 rounded-full">
              Notre promesse
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl font-black text-slate-900 mb-4">
              Notre Engagement
            </h2>
            <p className="text-slate-600 leading-relaxed">
              Nous nous engageons à fournir un service de qualité, sécurisé et
              efficace pour tous les voyageurs souhaitant découvrir les
              richesses du Niger ou y effectuer un séjour professionnel.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {commitments.map((c, i) => (
              <div
                key={i}
                className="bg-white p-6 rounded-2xl border border-slate-200 text-center hover:border-brand-300 hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mx-auto mb-4">
                  {c.icon}
                </div>
                <h3 className="font-bold text-slate-900 mb-1.5">{c.title}</h3>
                <p className="text-sm text-slate-600">{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Contact CTA */}
      <section className="pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-600 to-brand-700 p-10 md:p-12 text-center text-white shadow-xl">
            <div className="absolute -top-12 -right-12 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
            <div className="absolute -bottom-12 -left-12 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-black mb-3">
                Besoin d'aide ?
              </h2>
              <p className="text-white/85 mb-6 max-w-xl mx-auto">
                Notre équipe est disponible pour répondre à toutes vos questions
                et vous accompagner dans vos démarches.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center gap-2 bg-white text-brand-700 px-7 py-3 rounded-lg font-semibold hover:bg-brand-50 transition-colors shadow-md"
              >
                Contactez-nous
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
      <BackToTop />
    </div>
  );
};

export default AproScreen;
